import { useEffect, useState, useCallback, useMemo } from "react";
import { Loader2, Save, Minus } from "lucide-react";
import { toast } from "react-toastify";
import { accessService, type Permission, type Role } from "@/services/accessService";
import { useUiStore } from "@/store/uiStore";

function setsEqual(a: Set<string>, b: Set<string>): boolean {
  if (a.size !== b.size) return false;
  for (const item of a) {
    if (!b.has(item)) return false;
  }
  return true;
}

export function RolePermissionsTab() {
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [selectedRole, setSelectedRole] = useState<string>("");
  const [granted, setGranted] = useState<Set<string>>(new Set());
  const [savedGranted, setSavedGranted] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const { startPageLoading, stopPageLoading } = useUiStore();

  useEffect(() => {
    if (loading) startPageLoading();
    else stopPageLoading();
    return () => stopPageLoading();
  }, [loading, startPageLoading, stopPageLoading]);

  const [saving, setSaving] = useState(false);
  const dirty = useMemo(() => !setsEqual(granted, savedGranted), [granted, savedGranted]);

  useEffect(() => {
    Promise.all([accessService.listRoles(), accessService.listPermissions()]).then(([r, p]) => {
      setRoles(r);
      setPermissions(p);
      if (r.length > 0) setSelectedRole(r[0].name);
    }).finally(() => setLoading(false));
  }, []);

  const loadGranted = useCallback((roleName: string) => {
    if (!roleName) return;
    accessService.getRolePermissions(roleName).then((names) => {
      const loaded = new Set(names);
      setGranted(loaded);
      setSavedGranted(loaded);
    });
  }, []);

  useEffect(() => { loadGranted(selectedRole); }, [selectedRole, loadGranted]);

  const toggle = (name: string) => {
    setGranted((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  };

  const handleSelectAll = (items: Permission[]) => {
    setGranted((prev) => {
      const next = new Set(prev);
      items.forEach((p) => next.add(p.name));
      return next;
    });
  };

  const handleDeselectAll = (items: Permission[]) => {
    setGranted((prev) => {
      const next = new Set(prev);
      items.forEach((p) => next.delete(p.name));
      return next;
    });
  };

  const save = async () => {
    if (!selectedRole) return;
    setSaving(true);
    try {
      await accessService.setRolePermissions(selectedRole, [...granted]);
      setSavedGranted(new Set(granted));
      toast.success(`Permissions updated for role "${selectedRole}"`);
    } catch {
      toast.error("Failed to update role permissions");
    } finally {
      setSaving(false);
    }
  };

  // Group by resource
  const grouped = permissions.reduce<Record<string, Permission[]>>((acc, p) => {
    (acc[p.resource] ||= []).push(p);
    return acc;
  }, {});

  if (loading) return <LoadingSpinner />;

  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm sm:px-4">
        <select
          id="role-selector"
          value={selectedRole}
          onChange={(e) => {
            setSelectedRole(e.target.value);
          }}
          className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800 outline-none focus:border-[#009BF5]/40 focus:ring-2 focus:ring-[#009BF5]/15"
        >
          {roles.map((r) => (
            <option key={r.name} value={r.name}>
              {r.name}
            </option>
          ))}
        </select>
        <button
          id="save-role-permissions"
          onClick={save}
          disabled={!dirty || saving}
          className="flex items-center gap-2 rounded-xl bg-bridge-accent px-3.5 py-2 text-sm font-semibold text-white shadow-sm transition-opacity hover:bg-bridge-accent/90 disabled:opacity-40"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          Save Changes
        </button>
        {dirty && (
          <span className="text-xs font-medium text-amber-600">Unsaved changes</span>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {Object.entries(grouped)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([resource, items]) => {
            const selectedCount = items.filter((p) => granted.has(p.name)).length;
            const allSelected = selectedCount === items.length;
            const someSelected = selectedCount > 0 && selectedCount < items.length;

            return (
              <div
                key={resource}
                className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm"
              >
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-5 py-3">
                  <div className="flex items-center gap-2">
                    <span className="text-[15px] font-semibold capitalize text-slate-900">
                      {resource.replace(/_/g, " ")}
                    </span>
                    <span className="text-xs font-medium text-slate-400">
                      ({items.length})
                    </span>
                  </div>
                  {allSelected || someSelected ? (
                    <button
                      type="button"
                      onClick={() => handleDeselectAll(items)}
                      className="flex h-3.5 w-3.5 items-center justify-center rounded bg-bridge-accent text-white hover:bg-bridge-accent/90 transition-colors"
                      title="Deselect all"
                    >
                      <Minus size={10} strokeWidth={3} />
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => handleSelectAll(items)}
                      className="flex h-3.5 w-3.5 items-center justify-center rounded border border-slate-300 bg-white hover:border-slate-400 transition-colors"
                      title="Select all"
                    />
                  )}
                </div>
                <div className="grid grid-cols-2 gap-2 p-4 sm:grid-cols-3">
                  {items.map((p) => {
                    const checked = granted.has(p.name);
                    return (
                      <label
                        key={p.id}
                        htmlFor={`perm-${p.id}`}
                        className={[
                          "flex cursor-pointer select-none items-center gap-2 rounded-xl border px-3 py-2 text-xs font-semibold capitalize transition-colors",
                          checked
                            ? "border-bridge-accent/30 bg-bridge-accent/10 text-bridge-accent"
                            : "border-slate-200 text-slate-500 hover:border-slate-300 hover:bg-slate-50",
                        ].join(" ")}
                      >
                        <input
                          type="checkbox"
                          id={`perm-${p.id}`}
                          checked={checked}
                          onChange={() => toggle(p.name)}
                          className="accent-bridge-accent"
                        />
                        {p.action}
                      </label>
                    );
                  })}
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
}

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center py-24">
      <Loader2 size={24} className="animate-spin text-[#009BF5]" />
    </div>
  );
}
